/**
 * Phase 4 sync contracts, shared by @kortex/api and @kortex/pos-local so the
 * two never drift (docs/offline/PHASE-4-OFFLINE-ARCHITECTURE.md).
 *
 * Money/quantity fields here are integer paise / integer milli-units / integer
 * basis points, matching the on-device representation exactly (packages/
 * pos-local/src/db/schema.ts) - this boundary does no rupee<->paise
 * conversion, unlike the rest of the HTTP API, because the payload is a
 * pre-computed, already-frozen aggregate, not user input.
 */
import { z } from 'zod';
import { stationEnum } from './catalog.js';
import { OFFER_TYPES } from '../offers.js';
// kdsState/type/mode below reuse the canonical enums from operations.ts
// rather than redefining their own value sets - a redefined local
// `KDS_STATES` here once collided by name with operations.ts's own export
// and broke this package's build (both are re-exported via `export *` from
// schemas/index.ts), and even setting the name collision aside, a second
// definition is exactly the kind of divergence risk this shared package
// exists to prevent.
import { kdsStateEnum, orderTypeEnum, paymentModeEnum } from './operations.js';
export const SYNC_PROTOCOL_VERSION = 1;
/** Push batches are capped so one request stays well under the API's 1MB JSON limit and one bad batch cannot monopolise a sync cycle (prompt SS15/SS85). */
export const SYNC_PUSH_MAX_OPERATIONS = 25;
/** Pull master-data replicas are small tables (ADR-007); this is a sanity cap, not a real limit hit in practice. */
export const SYNC_PULL_MAX_MENU_ITEMS = 500;
const uuid = z.string().uuid();
const isoDateTime = z.string().datetime();
const paise = z.number().int();
const nonNegPaise = paise.min(0);
const milliQty = z.number().int();
const basisPoints = z.number().int().min(0).max(10_000);
const syncOfferTypeEnum = z.enum(OFFER_TYPES);
const syncChargeKeyEnum = z.enum(['PACKAGING', 'DELIVERY', 'SERVICE']);
export const SYNC_OPERATION_KINDS = ['SALE_AGGREGATE'];
export const syncOperationKindEnum = z.enum(SYNC_OPERATION_KINDS);
export const ORDER_AGGREGATE_STATUSES = ['BILLED', 'POSTED_TO_ROOM'];
export const INVOICE_AGGREGATE_STATUSES = ['PAID', 'POSTED_TO_ROOM'];
/** One order line as frozen at settle time (packages/pos-local/src/repos/orders.ts settle()). */
export const saleOrderLineSchema = z.object({
    id: uuid,
    menuItemId: uuid,
    name: z.string().trim().min(1).max(200),
    station: stationEnum,
    quantityMilli: milliQty.refine((v) => v > 0, 'Quantity must be positive'),
    unitPricePaise: nonNegPaise,
    discountPctBp: basisPoints,
    gstRateBp: basisPoints,
    hsn: z.string().trim().min(1).max(20),
    note: z.string().max(500).nullable().optional(),
    kdsState: kdsStateEnum,
    firedAt: isoDateTime,
    discountPaise: nonNegPaise,
    /** What an offer took off this line, frozen at settle time - see AppliedOffer. */
    offerDiscountPaise: nonNegPaise.default(0),
});
export const saleOrderAggregateSchema = z.object({
    id: uuid,
    number: z.string().trim().min(1).max(24),
    type: orderTypeEnum,
    status: z.enum(ORDER_AGGREGATE_STATUSES),
    tableId: uuid.nullable().optional(),
    stayId: uuid.nullable().optional(),
    covers: z.number().int().min(1).max(50),
    note: z.string().max(500).nullable().optional(),
    openedByUserId: uuid,
    openedAt: isoDateTime,
    closedAt: isoDateTime.nullable(),
    lines: z.array(saleOrderLineSchema).min(1).max(200),
});
export const saleInvoiceAggregateSchema = z.object({
    id: uuid,
    // Device series number (ADR-011, packages/pos-local/src/repos/sequences.ts
    // e.g. IN2627-T01-00042) - frozen on-device at settle time, never
    // reallocated server-side. Bound matches saleOrderAggregateSchema's own
    // `number` for consistency; the device's own format tops out well under it.
    number: z.string().trim().min(1).max(24),
    kind: z.literal('FNB'),
    status: z.enum(INVOICE_AGGREGATE_STATUSES),
    guestName: z.string().trim().min(1).max(120),
    guestGstin: z.string().trim().max(15).nullable().optional(),
    placeOfSupply: z.string().trim().length(2),
    isInterState: z.boolean(),
    subtotalPaise: nonNegPaise,
    discountPaise: nonNegPaise,
    cgstPaise: nonNegPaise,
    sgstPaise: nonNegPaise,
    igstPaise: nonNegPaise,
    roundOffPaise: paise,
    totalPaise: nonNegPaise,
    mode: paymentModeEnum,
    issuedByUserId: uuid,
    issuedAt: isoDateTime,
    discountReason: z.string().max(500).optional().default(''),
});
/** What one offer took off this ticket, frozen at settle time (Invoice.offers). */
export const saleAppliedOfferSchema = z.object({
    offerId: uuid,
    name: z.string().trim().min(1).max(120),
    type: syncOfferTypeEnum,
    discountPaise: nonNegPaise,
    times: z.number().int().min(1),
});
/** One extra charge line, frozen at settle time - amount and rate as they were
 *  actually printed, never recomputed from today's ChargeSettings on ingest. */
export const saleChargeLineSchema = z.object({
    key: syncChargeKeyEnum,
    label: z.string().trim().min(1).max(120),
    amountPaise: nonNegPaise,
    gstRateBp: basisPoints,
});
export const saleAggregatePayloadSchema = z.object({
    order: saleOrderAggregateSchema,
    invoice: saleInvoiceAggregateSchema,
    offers: z.array(saleAppliedOfferSchema).max(20).default([]),
    charges: z.array(saleChargeLineSchema).max(10).default([]),
});
/** The envelope frozen by SyncQueueRepository.prepare() - see packages/pos-local/src/sync/queue.ts. */
export const syncPushOperationSchema = z.object({
    opId: uuid,
    kind: syncOperationKindEnum,
    aggregateId: z.string().trim().min(1).max(64),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/, 'Expected a SHA-256 hex digest'),
    payload: saleAggregatePayloadSchema,
});
export const syncPushRequestSchema = z.object({
    deviceId: uuid,
    deviceCode: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9]{1,3}$/, 'Device code must be 2-4 chars, A-Z then A-Z0-9'),
    appVersion: z.string().trim().max(40).optional(),
    protocolVersion: z.number().int().min(1).max(SYNC_PROTOCOL_VERSION),
    operations: z.array(syncPushOperationSchema).min(1).max(SYNC_PUSH_MAX_OPERATIONS),
});
export const SYNC_OPERATION_RESULT_STATUSES = [
    'ACKNOWLEDGED',
    'ALREADY_PROCESSED',
    'REJECTED',
    'CONFLICT',
];
export const syncPullQuerySchema = z.object({
    deviceId: uuid,
    /** Opaque, base64 - the client must not interpret this, only echo it back. */
    since: z.string().trim().max(2000).optional(),
});
//# sourceMappingURL=sync.js.map